import { useEffect, useState } from 'react';
import { attendanceAPI, readersAPI } from '../services/api';
import LiveFeed from '../components/LiveFeed';
import { getRoleDisplayName } from '../utils/helpers';
import { AlertTriangle, Bus, CheckCircle, Clock, MapPin, RefreshCw, UserRoundCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const formatToday = () => new Date().toLocaleDateString([], {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="app-surface rounded-[1.6rem] p-6">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">{label}</p>
        <p className="mt-3 text-4xl font-black text-slate-900">{value ?? '—'}</p>
      </div>
      <div className={`${color} rounded-2xl p-3 shadow-lg shadow-slate-900/10`}>
        <Icon size={24} className="text-white" />
      </div>
    </div>
  </div>
);

const BreakdownPanel = ({ title, subtitle, rows, nameKey, totalKey, entityCategory }) => (
  <div className="app-surface rounded-[1.8rem] p-6">
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">{subtitle}</p>
      <h2 className="mt-2 text-xl font-black text-slate-900">{title}</h2>
    </div>

    {!rows?.length ? (
      <div className="mt-6 rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
        No attendance groups found.
      </div>
    ) : (
      <div className="mt-5 space-y-3">
        {rows.map((row) => {
          const total = Number(row[totalKey] || 0);
          const present = Number(row.present || 0);
          const rate = total > 0 ? Math.round((present / total) * 100) : 0;

          return (
            <div key={`${row[nameKey]}-${row.role || row.section_id}`} className="rounded-2xl border border-slate-100 bg-white/55 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900 capitalize">
                    {nameKey === 'role' ? getRoleDisplayName(row[nameKey], entityCategory) : String(row[nameKey] || 'Unassigned').replace(/_/g, ' ')}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {present} present • {row.on_leave || 0} leave • {row.not_present || 0} not present
                  </p>
                </div>
                <div className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-bold text-emerald-700">
                  {rate}%
                </div>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${rate}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const ReaderStatusBadge = ({ reader }) => {
  const isOnlineRecently = reader.last_seen
    ? (Date.now() - new Date(reader.last_seen).getTime()) < 5 * 60 * 1000
    : false;

  if (!reader.is_active) {
    return <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Inactive</span>;
  }

  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${isOnlineRecently ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
      {isOnlineRecently ? 'Online' : 'Idle'}
    </span>
  );
};

const Dashboard = () => {
  const { hasPermission, entityCategory, terminology } = useAuth();
  const [summary, setSummary] = useState(null);
  const [readers, setReaders] = useState([]);
  const canViewReaders = hasPermission('readers.view');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const summaryRes = await attendanceAPI.getSummary();
        setSummary(summaryRes.data);

        if (canViewReaders) {
          const readersRes = await readersAPI.getAll();
          setReaders(readersRes.data.readers || []);
        } else {
          setReaders([]);
        }
      } catch {
        toast.error('Failed to load dashboard data');
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [canViewReaders]);

  const totalAbsent = Math.max(
    Number(summary?.total_absent ?? ((summary?.total_users ?? 0) - (summary?.total_present ?? 0))),
    0
  );
  const insightCards = [
    { label: 'Present Today', value: summary?.total_present, color: 'bg-emerald-600', icon: CheckCircle },
    { label: 'Late Today', value: summary?.total_late ?? 0, color: 'bg-amber-500', icon: Clock },
    { label: 'On Leave', value: summary?.total_on_leave ?? 0, color: 'bg-sky-500', icon: UserRoundCheck },
    { label: 'Absent', value: totalAbsent, color: 'bg-rose-500', icon: AlertTriangle },
    { label: 'Bus Boarded', value: summary?.total_bus_boarded ?? 0, color: 'bg-teal-600', icon: Bus },
    { label: 'Office Entries', value: summary?.total_campus_entries ?? 0, color: 'bg-indigo-600', icon: MapPin },
  ];

  return (
    <div className="space-y-6">
      <div className="app-hero overflow-hidden rounded-[2rem] px-8 py-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="app-kicker">Operations Snapshot</p>
            <h1 className="mt-4 text-4xl font-black tracking-tight">Attendance command center</h1>
            <p className="mt-4 max-w-2xl text-sm text-white/78">
              Watch live scans, monitor reader health, and spot today&apos;s exceptions without opening detailed reports.
            </p>
          </div>
          <div className="rounded-2xl border border-white/12 bg-white/10 px-5 py-4">
            <p className="text-[11px] uppercase tracking-[0.24em] text-white/55">Live Refresh</p>
            <div className="mt-2 flex items-center gap-3">
              <RefreshCw size={18} className="text-white/70" />
              <div>
                <p className="text-lg font-black text-white">{formatToday()}</p>
                <p className="text-xs text-white/65">Updates every 30 seconds</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {insightCards.map(({ label, value, color, icon }) => (
          <StatCard key={label} icon={icon} label={label} value={value} color={color} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6">
        <LiveFeed />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <BreakdownPanel
          title="Today by role"
          subtitle="Official attendance"
          rows={summary?.present_by_role || []}
          nameKey="role"
          totalKey="total_users"
          entityCategory={entityCategory}
        />
        <BreakdownPanel
          title={`Today by ${terminology.group.toLowerCase()}`}
          subtitle={terminology.groups}
          rows={summary?.present_by_section || []}
          nameKey="section_name"
          totalKey="total_students"
          entityCategory={entityCategory}
        />
      </div>

      {canViewReaders && (
        <div className="app-surface rounded-[1.8rem] p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Hardware view</p>
              <h2 className="mt-2 text-xl font-black text-slate-900">Reader status</h2>
              <p className="text-sm text-slate-500 mt-1">Live device visibility across registered readers</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white/50 px-4 py-2 text-sm text-slate-600">
              {readers.length} total
            </div>
          </div>

          {readers.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
              No readers registered yet.
            </div>
          ) : (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3 text-left">Reader</th>
                    <th className="px-4 py-3 text-left">UID</th>
                    <th className="px-4 py-3 text-left">Location</th>
                    <th className="px-4 py-3 text-left">Last Seen</th>
                    <th className="px-4 py-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {readers.map((reader) => (
                    <tr key={reader.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{reader.name || 'Unnamed Reader'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{reader.reader_uid}</td>
                      <td className="px-4 py-3 text-gray-600">{reader.location_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {reader.last_seen ? new Date(reader.last_seen).toLocaleString() : 'Never'}
                      </td>
                      <td className="px-4 py-3">
                        <ReaderStatusBadge reader={reader} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
